import PropTypes from 'prop-types';
import { createContext, useContext, useMemo, useState } from 'react';

// ----------------------------------------------------------------------
// Global talent filters (economic sector + year) shared by the talent pages.
// Empty selection = "All". Years >= FORECAST_FROM are IO-model predictions
// and must always be flagged "P" wherever they render.
// ----------------------------------------------------------------------

export const ALL_YEARS = [2023, 2024, 2025, 2026, 2027, 2028, 2029, 2030];
export const FORECAST_FROM = 2027;
// worker skill / employment level (as submitted by EY on each occupation)
export const SKILL_LEVELS = ['Skilled', 'Semi-Skilled', 'Low-Skilled'];

const TalentFilterContext = createContext(null);

// default comparison on landing: current year vs last year (clamped to the
// data range, so e.g. in 2031 it falls back to the last two data years)
const nowYear = new Date().getFullYear();
const DEFAULT_YEARS = ALL_YEARS.includes(nowYear)
  ? [nowYear - 1, nowYear].filter((y) => ALL_YEARS.includes(y))
  : ALL_YEARS.slice(-2);

export function TalentFilterProvider({ children }) {
  const [sectors, setSectors] = useState([]); // empty = all sectors
  const [years, setYears] = useState(DEFAULT_YEARS); // pre-selected: last year vs this year
  const [division, setDivision] = useState(null); // third filter: set by clicking the map
  const [skills, setSkills] = useState([]); // fourth filter: skill / employment level

  const value = useMemo(
    () => ({ sectors, setSectors, years, setYears, division, setDivision, skills, setSkills }),
    [sectors, years, division, skills]
  );
  return <TalentFilterContext.Provider value={value}>{children}</TalentFilterContext.Provider>;
}

TalentFilterProvider.propTypes = { children: PropTypes.node };

export function useTalentFilters() {
  const ctx = useContext(TalentFilterContext);
  // pages can render outside the provider (e.g. superadmin tools) — degrade to "all"
  return (
    ctx || {
      sectors: [], setSectors: () => {}, years: [], setYears: () => {},
      division: null, setDivision: () => {}, skills: [], setSkills: () => {},
    }
  );
}
